import React from 'react';

/**
 * Skeleton — animated loading placeholder.
 *
 * Per Design Principles §3.15.2 we use skeletons (not spinners) for any
 * list, table, chart, or card. Skeletons match the eventual content's
 * layout — gray bars where text will be, gray rectangles where cards
 * will be, gray circles where avatars/icons will be.
 *
 * Variants:
 *   - text   : single (or multi-line) gray bar matching text height
 *   - rect   : rectangular block (cards, charts, images)
 *   - circle : avatar or icon placeholder
 *
 * The pulse animation uses Tailwind's animate-pulse (already in CRA's
 * Tailwind base). All elements are aria-hidden so screen readers don't
 * announce decorative loading state — pair the skeleton container with
 * its own aria-busy="true" if needed.
 *
 * @param {Object} props
 * @param {'text'|'rect'|'circle'} [props.variant='text']
 * @param {number|string}          [props.width]
 * @param {number|string}          [props.height]
 * @param {number}                 [props.lines=1] — multi-line text only
 * @param {string}                 [props.className]
 */
export function Skeleton({
  variant = 'text',
  width,
  height,
  lines = 1,
  className = '',
}) {
  const tone = 'bg-v2-muted';

  const styleFor = (w, h) => ({
    width: w == null ? '100%' : (typeof w === 'number' ? `${w}px` : w),
    height: h == null ? undefined : (typeof h === 'number' ? `${h}px` : h),
  });

  if (variant === 'circle') {
    const dim = height || width || 32;
    return (
      <span
        aria-hidden="true"
        className={'block rounded-full animate-pulse ' + tone + ' ' + className}
        style={{ width: typeof dim === 'number' ? `${dim}px` : dim, height: typeof dim === 'number' ? `${dim}px` : dim }}
      />
    );
  }

  if (variant === 'rect') {
    return (
      <span
        aria-hidden="true"
        className={'block rounded-v2-sm animate-pulse ' + tone + ' ' + className}
        style={styleFor(width, height || 60)}
      />
    );
  }

  // text variant
  if (lines <= 1) {
    return (
      <span
        aria-hidden="true"
        className={'inline-block rounded animate-pulse ' + tone + ' ' + className}
        style={styleFor(width || '70%', height || '0.875em')}
      />
    );
  }

  return (
    <span aria-hidden="true" className={'block space-y-1.5 ' + className}>
      {Array.from({ length: lines }).map((_, i) => (
        <span
          key={i}
          className={'block rounded animate-pulse ' + tone}
          style={styleFor(i === lines - 1 ? '60%' : '100%', '0.875em')}
        />
      ))}
    </span>
  );
}

export default Skeleton;
